import React, { useCallback, useEffect, useState } from 'react';
import usePublishedRoles from '../../hooks/usePublishedRoles';
import { assignPrimaryRole } from '../../services/roleAssignmentService';
import { trackRoleSelected } from '../../utils/analytics';
import './RolePickerModal.css';

/**
 * Post-signup role picker — single-select among published launch roles.
 * Shown when a registered user has no `primaryRoleId` yet.
 */
export default function RolePickerModal({ open, userId, onComplete }) {
  const { roles, loading, error } = usePublishedRoles();
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedRoleId(null);
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (!userId || !selectedRoleId || submitting) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await assignPrimaryRole(userId, selectedRoleId);
      trackRoleSelected({ role_id: selectedRoleId, surface: 'role-picker-modal' });
      onComplete?.(selectedRoleId);
    } catch (err) {
      console.error('[RolePickerModal] assignPrimaryRole failed:', err);
      setSubmitError('Could not save your role. Please try again.');
      setSubmitting(false);
    }
  }, [userId, selectedRoleId, submitting, onComplete]);

  if (!open) return null;

  return (
    <div
      className="role-picker-modal__backdrop"
      role="presentation"
      aria-hidden={!open}
    >
      <div
        className="role-picker-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-picker-modal-title"
        aria-describedby="role-picker-modal-desc"
      >
        <div className="role-picker-modal__header">
          <h2 id="role-picker-modal-title" className="role-picker-modal__title">
            What do you want to become?
          </h2>
          <p id="role-picker-modal-desc" className="role-picker-modal__subtitle">
            Pick your main learning path. You&apos;ll land in Learn with your tracks ready.
          </p>
        </div>

        {loading ? (
          <p className="role-picker-modal__status">Loading roles…</p>
        ) : null}

        {!loading && error && roles.length === 0 ? (
          <p className="role-picker-modal__status role-picker-modal__status--error">
            Could not load roles. Refresh and try again.
          </p>
        ) : null}

        {!loading && roles.length > 0 ? (
          <ul className="role-picker-modal__list" aria-label="Learning roles">
            {roles.map((role) => {
              const isSelected = selectedRoleId === role.roleId;
              return (
                <li key={role.roleId} className="role-picker-modal__item">
                  <button
                    type="button"
                    className={[
                      'role-picker-modal__card',
                      isSelected ? 'role-picker-modal__card--selected' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedRoleId(role.roleId)}
                    disabled={submitting}
                  >
                    <span className="role-picker-modal__card-icon-wrap" aria-hidden>
                      {role.iconClass ? (
                        <i className={`ti ${role.iconClass} role-picker-modal__card-icon`} />
                      ) : (
                        <i className="ti ti-user role-picker-modal__card-icon" />
                      )}
                    </span>
                    <span className="role-picker-modal__card-body">
                      <span className="role-picker-modal__card-title">{role.title}</span>
                      {role.promise ? (
                        <span className="role-picker-modal__card-promise">{role.promise}</span>
                      ) : null}
                    </span>
                    <span
                      className={[
                        'role-picker-modal__card-check',
                        isSelected ? 'role-picker-modal__card-check--on' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {submitError ? (
          <p className="role-picker-modal__submit-error" role="alert">
            {submitError}
          </p>
        ) : null}

        <div className="role-picker-modal__footer">
          <button
            type="button"
            className="role-picker-modal__confirm-btn"
            onClick={handleConfirm}
            disabled={!selectedRoleId || submitting || loading || roles.length === 0}
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
